(() => {
  const GROUP_SELECTOR =
    '[data-reveal-group]';

  const NESTED_SELECTOR =
    '[data-reveal-group-nested]';

  const MOBILE_REVEAL_QUERY =
    window.matchMedia(
      '(max-width: 767px)'
    );

  const DEFAULT_STAGGER_MS = 65;
  const DEFAULT_DISTANCE = '16px';
  const DEFAULT_START = 'top 78%';
  const DEFAULT_DURATION = 0.65;
  const DEFAULT_EASE = 'power3.out';

  const MAX_RETRIES = 40;
  const RETRY_DELAY = 100;

  let dependenciesRegistered = false;

  let currentContext = null;
  let currentRoot = null;
  let currentScroller = null;

  let initFrame = null;
  let retryTimer = null;
  let retryCount = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function registerDependencies() {
    if (
      !window.gsap ||
      !window.ScrollTrigger
    ) {
      return false;
    }

    if (!dependenciesRegistered) {
      gsap.registerPlugin(
        ScrollTrigger
      );

      dependenciesRegistered = true;
    }

    return true;
  }


  /* =========================================
     ACTIVE PAGE
     ========================================= */

  function getPageRoot() {
    return (
      document.querySelector(
        '[data-barba="container"]'
      ) ||
      document.body
    );
  }


  /* =========================================
     SCROLL ROOT
     ========================================= */

  function getScroller() {
    const scrollElement =
      window.wamboltScrollElement;

    if (
      scrollElement instanceof
      Element
    ) {
      return scrollElement;
    }

    return null;
  }


  /* =========================================
     VISIBILITY
     ========================================= */

  function isRendered(element) {
    if (
      !(element instanceof Element)
    ) {
      return false;
    }

    const styles =
      window.getComputedStyle(
        element
      );

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden'
    );
  }


  /* =========================================
     RESPONSIVE REVEAL MODE
     ========================================= */

  function isRevealGroupEnabled(
    groupElement
  ) {
    const mobileOnly =
      groupElement.getAttribute(
        'data-reveal-mobile-only'
      ) === 'true';

    if (
      mobileOnly &&
      !MOBILE_REVEAL_QUERY.matches
    ) {
      return false;
    }

    return true;
  }


  /* =========================================
     NUMBERS
     ========================================= */

  function getNumberAttribute(
    element,
    attribute,
    fallback
  ) {
    const value =
      Number.parseFloat(
        element.getAttribute(
          attribute
        )
      );

    return Number.isFinite(value)
      ? value
      : fallback;
  }


  /* =========================================
     GROUPS
     ========================================= */

  function getGroups(root) {
    if (!root) {
      return [];
    }

    const groups = [];

    if (
      root instanceof Element &&
      root.matches(GROUP_SELECTOR)
    ) {
      groups.push(root);
    }

    if (
      typeof root.querySelectorAll ===
      'function'
    ) {
      groups.push(
        ...root.querySelectorAll(
          GROUP_SELECTOR
        )
      );
    }

    return groups;
  }


  /* =========================================
     ANIMATION TARGET
     ========================================= */

  function getAnimationTarget(
    child,
    groupElement
  ) {
    const selector =
      child.getAttribute(
        'data-reveal-target'
      ) ||
      groupElement.getAttribute(
        'data-reveal-target'
      );

    if (!selector) {
      return child;
    }

    try {
      return (
        child.querySelector(
          selector
        ) ||
        child
      );
    } catch (error) {
      return child;
    }
  }


  /* =========================================
     ITEM CONFIG
     ========================================= */

  function getItemConfig(
    child,
    groupElement,
    defaultDistance
  ) {
    const target =
      getAnimationTarget(
        child,
        groupElement
      );

    const groupFadeOnly =
      groupElement.getAttribute(
        'data-reveal-fade-only'
      ) === 'true';

    const itemFadeOnly =
      child.getAttribute(
        'data-reveal-fade-only'
      ) === 'true';

    return {
      target,

      fadeOnly:
        groupFadeOnly ||
        itemFadeOnly,

      distance:
        child.getAttribute(
          'data-distance'
        ) ||
        target.getAttribute(
          'data-distance'
        ) ||
        defaultDistance,
    };
  }


  /* =========================================
     REVEAL SEQUENCE
     ========================================= */

  function buildSequence(
    groupElement
  ) {
    const groupDistance =
      groupElement.getAttribute(
        'data-distance'
      ) ||
      DEFAULT_DISTANCE;

    const groupStagger =
      getNumberAttribute(
        groupElement,
        'data-stagger',
        DEFAULT_STAGGER_MS
      ) / 1000;

    const directChildren =
      Array.from(
        groupElement.children
      );

    const sequence = [];

    let currentSlot = 0;

    directChildren.forEach(
      (child) => {
        if (
          child.getAttribute(
            'data-ignore'
          ) === 'true'
        ) {
          return;
        }

        if (!isRendered(child)) {
          return;
        }


        /* =========================================
           NESTED GROUP
           ========================================= */

        if (
          child.matches(
            NESTED_SELECTOR
          )
        ) {
          const nestedStagger =
            getNumberAttribute(
              child,
              'data-stagger',
              DEFAULT_STAGGER_MS
            ) / 1000;

          const nestedDistance =
            child.getAttribute(
              'data-distance'
            ) ||
            groupDistance;

          const nestedChildren =
            Array.from(
              child.children
            ).filter(
              (nestedChild) => {
                return (
                  nestedChild.getAttribute(
                    'data-ignore'
                  ) !== 'true' &&
                  isRendered(
                    nestedChild
                  )
                );
              }
            );

          nestedChildren.forEach(
            (
              nestedChild,
              nestedIndex
            ) => {
              const config =
                getItemConfig(
                  nestedChild,
                  child,
                  nestedDistance
                );

              sequence.push({
                target:
                  config.target,

                time:
                  currentSlot +
                  nestedIndex *
                    nestedStagger,

                distance:
                  config.distance,

                fadeOnly:
                  config.fadeOnly,
              });
            }
          );

          currentSlot +=
            Math.max(
              groupStagger,
              nestedChildren.length *
                nestedStagger
            );

          return;
        }


        /* =========================================
           STANDARD ITEM
           ========================================= */

        const config =
          getItemConfig(
            child,
            groupElement,
            groupDistance
          );

        sequence.push({
          target:
            config.target,

          time:
            currentSlot,

          distance:
            config.distance,

          fadeOnly:
            config.fadeOnly,
        });

        currentSlot +=
          groupStagger;
      }
    );

    return sequence;
  }


  /* =========================================
     INITIALIZE GROUP
     ========================================= */

  function initGroup(
    groupElement,
    scroller,
    prefersReducedMotion
  ) {
    if (
      prefersReducedMotion ||
      !isRendered(groupElement) ||
      !isRevealGroupEnabled(
        groupElement
      )
    ) {
      return;
    }

    const sequence =
      buildSequence(
        groupElement
      );

    if (!sequence.length) {
      return;
    }

    const triggerStart =
      groupElement.getAttribute(
        'data-start'
      ) ||
      DEFAULT_START;

    const duration =
      getNumberAttribute(
        groupElement,
        'data-duration',
        DEFAULT_DURATION
      );


    /* =========================================
       INITIAL STATE
       ========================================= */

    sequence.forEach(
      (item) => {
        if (item.fadeOnly) {
          gsap.set(
            item.target,
            {
              autoAlpha: 0,
            }
          );

          return;
        }

        gsap.set(
          item.target,
          {
            y:
              item.distance,

            autoAlpha: 0,
          }
        );
      }
    );


    /* =========================================
       TRIGGER
       ========================================= */

    ScrollTrigger.create({
      trigger:
        groupElement,

      start:
        triggerStart,

      once: true,

      /*
       * Reveal triggers should measure after
       * structural pinning ScrollTriggers.
       */
      refreshPriority: -10,

      ...(scroller
        ? {
            scroller,
          }
        : {}),

      onEnter() {
        const timeline =
          gsap.timeline({
            defaults: {
              duration,
              ease:
                DEFAULT_EASE,
            },
          });

        sequence.forEach(
          (item) => {
            if (item.fadeOnly) {
              timeline.to(
                item.target,
                {
                  autoAlpha: 1,

                  onComplete() {
                    gsap.set(
                      item.target,
                      {
                        clearProps:
                          'opacity,visibility',
                      }
                    );
                  },
                },
                item.time
              );

              return;
            }

            timeline.to(
              item.target,
              {
                y: 0,
                autoAlpha: 1,

                onComplete() {
                  gsap.set(
                    item.target,
                    {
                      clearProps:
                        'transform,opacity,visibility',
                    }
                  );
                },
              },
              item.time
            );
          }
        );
      },
    });
  }


  /* =========================================
     DESTROY
     ========================================= */

  function destroyReveal() {
    if (initFrame !== null) {
      window.cancelAnimationFrame(
        initFrame
      );

      initFrame = null;
    }

    if (retryTimer !== null) {
      window.clearTimeout(
        retryTimer
      );

      retryTimer = null;
    }

    if (currentContext) {
      currentContext.revert();

      currentContext = null;
    }

    currentRoot = null;
    currentScroller = null;
  }


  /* =========================================
     INITIALIZE
     ========================================= */

  function initReveal(
    force = false
  ) {
    if (!registerDependencies()) {
      return false;
    }

    const root =
      getPageRoot();

    const scroller =
      getScroller();

    if (
      !force &&
      currentContext &&
      currentRoot === root &&
      currentScroller === scroller
    ) {
      ScrollTrigger.refresh();

      return true;
    }

    if (currentContext) {
      currentContext.revert();

      currentContext = null;
    }

    currentRoot =
      root;

    currentScroller =
      scroller;

    const prefersReducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    currentContext =
      gsap.context(
        () => {
          getGroups(root).forEach(
            (groupElement) => {
              initGroup(
                groupElement,
                scroller,
                prefersReducedMotion
              );
            }
          );
        },
        root
      );

    window.requestAnimationFrame(
      () => {
        ScrollTrigger.refresh();
      }
    );

    return true;
  }


  /* =========================================
     RETRY
     ========================================= */

  function tryInit(
    force = false
  ) {
    if (
      initReveal(force)
    ) {
      retryCount = 0;

      if (
        retryTimer !== null
      ) {
        window.clearTimeout(
          retryTimer
        );

        retryTimer = null;
      }

      return;
    }

    if (
      retryCount >=
      MAX_RETRIES
    ) {
      console.warn(
        'Wambolt reveal could not initialize because GSAP or ScrollTrigger is unavailable.'
      );

      return;
    }

    retryCount += 1;

    retryTimer =
      window.setTimeout(
        () => {
          retryTimer = null;

          tryInit(force);
        },
        RETRY_DELAY
      );
  }


  /* =========================================
     QUEUED INITIALIZATION
     ========================================= */

  function scheduleInit(
    force = false
  ) {
    if (initFrame !== null) {
      window.cancelAnimationFrame(
        initFrame
      );
    }

    initFrame =
      window.requestAnimationFrame(
        () => {
          initFrame = null;

          tryInit(force);
        }
      );
  }


  /* =========================================
     RESPONSIVE REVEAL CHANGES
     ========================================= */

  function handleRevealModeChange() {
    scheduleInit(true);
  }

  if (
    typeof MOBILE_REVEAL_QUERY
      .addEventListener ===
      'function'
  ) {
    MOBILE_REVEAL_QUERY
      .addEventListener(
        'change',
        handleRevealModeChange
      );
  } else if (
    typeof MOBILE_REVEAL_QUERY
      .addListener ===
      'function'
  ) {
    MOBILE_REVEAL_QUERY
      .addListener(
        handleRevealModeChange
      );
  }


  /* =========================================
     INITIAL PAGE
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleInit();
      },
      {
        once: true,
      }
    );
  } else {
    scheduleInit();
  }


  /* =========================================
     BARBA
     ========================================= */

  window.addEventListener(
    'wambolt:page:before-leave',
    destroyReveal
  );

  window.addEventListener(
    'wambolt:page:enter',
    () => {
      scheduleInit(true);
    }
  );


  /* =========================================
     LENIS
     ========================================= */

  window.addEventListener(
    'wambolt:lenis:ready',
    () => {
      const root =
        getPageRoot();

      const scroller =
        getScroller();

      if (
        currentContext &&
        currentRoot === root &&
        currentScroller === scroller
      ) {
        window.requestAnimationFrame(
          () => {
            ScrollTrigger.refresh();
          }
        );

        return;
      }

      scheduleInit(true);
    }
  );


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  if (
    document.documentElement
      .classList.contains(
        'shopify-design-mode'
      )
  ) {
    document.addEventListener(
      'shopify:section:load',
      () => {
        scheduleInit(true);
      }
    );

    document.addEventListener(
      'shopify:section:unload',
      () => {
        scheduleInit(true);
      }
    );
  }
})();